/**
 * i18n.ts — lire un cas socle dans la langue du lecteur.
 *
 * ── Pourquoi un helper plutôt qu'un ternaire sur place ───────────────────────
 * Le dépôt a déjà payé ce prix : le motif `lang === "fr" && x.labelFr ? x.labelFr : x.label` s'est
 * dupliqué par copier-coller dans quatre écrans, et il IGNORE la variante anglaise — l'anglais
 * affichait du français sous des titres traduits. Un test de garde interdit désormais ce motif
 * (`tests/i18n-no-raw-label.test.ts`). Les champs de la banque passent donc tous par ici.
 *
 * ── La règle de repli ────────────────────────────────────────────────────────
 * L'anglais retombe sur le français quand il manque, jamais l'inverse pour les champs dont le
 * français EST la colonne de référence (`display_name`, `scenario_instruction`). Conséquence
 * voulue : un cas partiellement traduit reste jouable — il ne casse pas, il se dégrade.
 */

export type LangueCas = "fr" | "en";

/** Normalise ce que l'appelant a sous la main (`users.language`, `?lang=`, `i18n.language`). */
export const langueCas = (v?: string | null): LangueCas =>
  (v ?? "fr").toLowerCase().startsWith("en") ? "en" : "fr";

const rempli = (v?: string | null): string => (v ?? "").trim();

/**
 * Les champs de la banque qui existent en deux variantes explicites (`*Fr` / `*En`).
 * Ajouter une entrée ici est le SEUL endroit à modifier pour rendre un nouveau champ bilingue.
 */
const PAIRES = {
  userCompanyName: ["userCompanyNameFr", "userCompanyNameEn"],
  userCompanyProfile: ["userCompanyProfileFr", "userCompanyProfileEn"],
  userCompanyMarketNote: ["userCompanyMarketNoteFr", "userCompanyMarketNoteEn"],
  // L'entreprise du PROSPECT (migration 20260909-1059) — vide pour un particulier (B2C).
  prospectCompanyName: ["prospectCompanyNameFr", "prospectCompanyNameEn"],
  prospectCompanyProfile: ["prospectCompanyProfileFr", "prospectCompanyProfileEn"],
  // La situation de départ (migration 20261005-1619).
  situationSetup: ["situationSetupFr", "situationSetupEn"],
  situationTrigger: ["situationTriggerFr", "situationTriggerEn"],
  objectiveHint: ["objectiveHintFr", "objectiveHintEn"],
  personaSituation: ["personaSituationFr", "personaSituationEn"],
  // L'enjeu caché et le comportement de l'interlocuteur (migration 20261006-1934) — longtemps
  // rangés dans des champs de la persona qu'ils détournaient.
  hiddenStake: ["hiddenStakeFr", "hiddenStakeEn"],
  personaBehavior: ["personaBehaviorFr", "personaBehaviorEn"],
} as const;

export type ChampCas = keyof typeof PAIRES;

/** Lit un champ `*Fr`/`*En` du cas. Repli sur l'autre langue plutôt qu'une chaîne vide. */
export function pickCasField(
  cas: Record<string, any> | null | undefined,
  champ: ChampCas,
  langue: string | undefined,
): string {
  if (!cas) return "";
  const [cleFr, cleEn] = PAIRES[champ];
  const fr = rempli(cas[cleFr]);
  const en = rempli(cas[cleEn]);
  return langueCas(langue) === "en" ? en || fr : fr || en;
}

/**
 * Les deux champs dont le FRANÇAIS est la colonne de référence, partagée avec tous les cas
 * clients : seule la variante anglaise a été ajoutée (migration 20260902-1745).
 */
export function pickCasLegacyField(
  cas: Record<string, any> | null | undefined,
  champ: "displayName" | "scenarioInstruction",
  langue: string | undefined,
): string {
  if (!cas) return "";
  const base = rempli(cas[champ]);
  const en = rempli(cas[`${champ}En`]);
  return langueCas(langue) === "en" ? en || base : base;
}

/**
 * Le scénario réécrit à la main, dans la langue demandée — et SANS repli d'une langue sur l'autre.
 *
 * ── Pourquoi la règle de repli s'inverse ici ─────────────────────────────────
 * Partout ailleurs, l'anglais manquant retombe sur le français : un cas à moitié traduit reste
 * jouable. Appliqué à un texte figé, ce repli servirait un brief ENTIÈREMENT EN FRANÇAIS à un
 * apprenant anglophone — plus mauvais que la version anglaise composée automatiquement, qui est
 * au moins dans sa langue. Une face sans texte figé dans SA langue reste donc composée par
 * `buildSocleScenario`.
 *
 * Conséquence assumée : réécrire le français ne change rien à la session anglaise tant que la
 * traduction n'a pas été relancée. C'est visible dans la fiche, qui affiche les deux langues.
 */
export function texteFigeDuCas(
  cas: Record<string, any> | null | undefined,
  langue: string | undefined,
): { visible: string; agent: string } {
  if (!cas) return { visible: "", agent: "" };
  const suffixe = langueCas(langue) === "en" ? "En" : "Fr";
  return {
    visible: rempli(cas[`scenarioVisibleMd${suffixe}`]),
    agent: rempli(cas[`scenarioAgentMd${suffixe}`]),
  };
}

/**
 * Le titre affiché d'un cas, avec le repli déjà en place avant ce chantier : nom du cas, sinon
 * l'entreprise du cas. Rassemblé ici pour que le sélecteur et l'écran d'administration ne
 * divergent pas.
 */
export function titreCas(cas: Record<string, any> | null | undefined, langue: string | undefined): string {
  return pickCasLegacyField(cas, "displayName", langue) || pickCasField(cas, "userCompanyName", langue);
}

/**
 * Un cas est-il lisible dans cette langue ? Sert au filtre d'exposition : un socle sans anglais
 * n'est pas servi à un utilisateur en anglais, il lirait du français sous une interface traduite.
 */
export function casEstLisibleEn(cas: Record<string, any> | null | undefined): boolean {
  return !!cas && rempli(cas.userCompanyProfileEn).length > 0 && rempli(cas.displayNameEn).length > 0;
}
